import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';
import http from '@/services/http';
import type { ApiResponse } from '@/types/types';
import { LIVE_RECORD_POLL_MS } from '@/composables/searchInspectorRegistry';
import { liveRecords } from '@/composables/liveRecords/liveRecords';

/**
 * Keep the visible search rows fresh without re-running the user's search.
 *
 * The search endpoint re-flattens live values (e.g. a LocationZonePlayer's
 * check-in state from Redis) on EVERY response, so results are only stale
 * because the search page fetches once. This composable re-fetches the rows
 * currently on screen — by id, via the same search endpoint — on the
 * inspector's poll cadence and merges the fresh values in place. The row set
 * itself is frozen: polling never adds, removes, or reorders rows, so the
 * table can't shift under the user; the next real search reconciles.
 *
 * Polling is size-gated rather than entity-gated: any result set whose search
 * payload is under LIVE_POLL_MAX_PAYLOAD_BYTES polls, larger ones don't.
 */

export const LIVE_POLL_MAX_PAYLOAD_BYTES = 1_000_000;

/** Consecutive poll failures before the indicator reports the data as stale. */
export const LIVE_POLL_FAILING_AFTER_CONSECUTIVE_FAILURES = 5;

export type SearchLivePollState =
    /** No rows to poll (empty results / nothing loaded yet). */
    | 'idle'
    /** Polling on the interval; row values are live. */
    | 'active'
    /** Result payload is over the limit — polling is off for this result set. */
    | 'payload-too-large'
    /** Polls keep failing — values shown may be stale. Recovers on the next success. */
    | 'failing';

const payloadEncoder = new TextEncoder();

export interface SearchResultsLivePollingParams {
    entitySlug: ComputedRef<string>;
    /** The search query's response — rows + meta as returned by useSearchApi. */
    searchData: Ref<ApiResponse | undefined>;
    /**
     * The entity id whose 'model-record' cache entries should overlay matching
     * rows. An open inspector/detail pane polls its record FASTER than the row
     * poll (INSPECTED_RECORD_POLL_MS vs LIVE_RECORD_POLL_MS); bridging its
     * responses in keeps the inspected row and the detail pane in lockstep
     * instead of drifting on independent 30s clocks.
     */
    recordEntityId?: Ref<string> | ComputedRef<string>;
}

export function useSearchResultsLivePolling({ entitySlug, searchData, recordEntityId }: SearchResultsLivePollingParams) {
    const baseRows = computed<Record<string, unknown>[]>(() => searchData.value?.data ?? []);

    /** Byte size of the current search response — the gate for enabling polling. */
    const payloadBytes = computed(() =>
        searchData.value ? payloadEncoder.encode(JSON.stringify(searchData.value)).byteLength : 0,
    );

    const rowIds = computed(() =>
        baseRows.value
            .map((row) => row.id as number | string | null | undefined)
            .filter((id): id is number | string => id !== null && id !== undefined),
    );

    const isPollingEligible = computed(
        () =>
            rowIds.value.length > 0 &&
            payloadBytes.value < LIVE_POLL_MAX_PAYLOAD_BYTES &&
            Boolean(entitySlug.value),
    );

    const pollQuery = useQuery({
        // The ids are part of the key, so a new search / page / entity gets a fresh
        // query entry — its own interval and failure counter — and an in-flight
        // response for a previous row set can never bleed into the current one.
        queryKey: computed(() => ['search-live-poll', entitySlug.value, rowIds.value.join(',')]),
        queryFn: async () => {
            const response = await http.post(`/api/${entitySlug.value}/search`, {
                perPage: rowIds.value.length,
                withTotal: false,
                filter: {
                    ruleScopes: {
                        id: 'root',
                        scopeType: 'and',
                        rules: [
                            { id: 'live-poll', column: 'id', criterion: 'in', value: rowIds.value, field: null },
                        ],
                    },
                },
            });
            return ((response.data as ApiResponse).data ?? []) as Record<string, unknown>[];
        },
        enabled: isPollingEligible,
        // Refetched as soon as the backend announces a write to one of these rows (SAU-530).
        meta: computed(() => liveRecords(entitySlug.value ?? '', rowIds.value)),
        refetchInterval: LIVE_RECORD_POLL_MS,
        // vue-query skips interval ticks while the tab is hidden (focusManager
        // reads visibilityState), so a backgrounded tab froze at its last poll
        // and every player looked offline ("6m since check-in") the moment the
        // user came back — until the next tick landed up to 30s later. Keep
        // ticking while hidden (the payload gate bounds the cost), and refetch
        // immediately on refocus (per-query override of the app-level
        // refetchOnWindowFocus: false) in case the browser throttled the
        // background timer anyway.
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
        // Seed with the rows the search just returned — they carry the same live
        // values a poll would fetch — so enabling the poll doesn't fire an
        // immediate duplicate request; the first real poll lands one interval later.
        initialData: () => baseRows.value,
        staleTime: LIVE_RECORD_POLL_MS,
        retry: 0,
    });

    // vue-query's failureCount resets when each new fetch STARTS, so it can't count
    // consecutive failed ticks — track them ourselves: +1 per errored fetch, reset to 0
    // on any successful data (a poll response, or the seed of a new result set).
    const consecutiveFailureCount = ref(0);
    watch(
        pollQuery.errorUpdatedAt,
        (timestamp, previousTimestamp) => {
            if ((timestamp ?? 0) > (previousTimestamp ?? 0)) {
                consecutiveFailureCount.value++;
            }
        },
        { flush: 'sync' },
    );
    watch(
        pollQuery.dataUpdatedAt,
        () => {
            consecutiveFailureCount.value = 0;
        },
        { flush: 'sync' },
    );

    // Timestamp of the newest base search response. The overlay only applies while the
    // poll data is at least this fresh — otherwise a refetch of the base search (e.g. on
    // window focus) would be shadowed by an older poll response and on-screen values
    // would visibly regress until the next tick.
    const searchDataUpdatedAt = ref(0);
    watch(
        searchData,
        () => {
            searchDataUpdatedAt.value = Date.now();
        },
        { flush: 'sync' },
    );

    /**
     * Freshest inspected-record payloads by row id, harvested from the shared
     * vue-query cache: any surface polling a record via useModelRecordQuery
     * (the split-pane inspector, the side-peek, the overlay header) writes to
     * ['model-record', entityId, id], and those fetches go through the SAME
     * search endpoint, so the payload shape matches the search rows exactly.
     */
    const inspectedRowsById = ref(new Map<string, { row: Record<string, unknown>; at: number }>());

    const queryClient = useQueryClient();
    const unsubscribeInspected = queryClient.getQueryCache().subscribe((event) => {
        if (recordEntityId === undefined || event.type !== 'updated') {
            return;
        }
        const key = event.query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'model-record' || key[1] !== recordEntityId.value) {
            return;
        }
        const data = event.query.state.data as Record<string, unknown> | undefined;
        if (!data || data.id == null) {
            return;
        }
        const next = new Map(inspectedRowsById.value);
        next.set(String(data.id), { row: data, at: event.query.state.dataUpdatedAt });
        // Only rows on screen matter; prune anything that scrolled away so the
        // map cannot grow across long browsing sessions.
        const visible = new Set(rowIds.value.map(String));
        for (const id of next.keys()) {
            if (!visible.has(id)) {
                next.delete(id);
            }
        }
        inspectedRowsById.value = next;
    });
    onScopeDispose(unsubscribeInspected);

    // Entity switches invalidate the harvested payloads (different row shape).
    if (recordEntityId !== undefined) {
        watch(recordEntityId, () => {
            inspectedRowsById.value = new Map();
        });
    }

    /** Latest known row per id — poll results, or the seeded search rows before the first tick. */
    const polledRowsById = computed(() => {
        const rowsById = new Map<string, Record<string, unknown>>();
        if (pollQuery.dataUpdatedAt.value < searchDataUpdatedAt.value) {
            // The base search response is newer than the last poll: render it as-is
            // (empty overlay) instead of shadowing it with older values.
            return rowsById;
        }
        for (const row of pollQuery.data.value ?? []) {
            rowsById.set(String(row.id), row);
        }
        return rowsById;
    });

    /**
     * The rows to render: the search's row set (frozen order and membership)
     * with each row's values refreshed from the latest poll response.
     */
    const displayRows = computed(() =>
        baseRows.value.map((row) => {
            const id = String(row.id);
            const polled = polledRowsById.value.get(id);
            const inspected = inspectedRowsById.value.get(id);
            // Newest source wins: an open inspector polls faster than the row
            // poll, so its payload is usually the freshest — but never older
            // than the base search response (same guard as the poll overlay).
            const polledAt = polled ? pollQuery.dataUpdatedAt.value : 0;
            const inspectedAt =
                inspected && inspected.at >= searchDataUpdatedAt.value ? inspected.at : 0;
            // Ties go to the inspected record: it is the per-record source an
            // open detail pane is actively refreshing.
            if (inspectedAt > 0 && inspectedAt >= polledAt) {
                return inspected!.row;
            }
            return polled ?? row;
        }),
    );

    /** A row the latest poll no longer returned (deleted / de-scoped) — mark it, keep last values. */
    function isRowMissing(row: Record<string, unknown>): boolean {
        // A successful-but-EMPTY response for ids we know existed moments ago is a
        // backend/index hiccup, not N simultaneous deletions — never dim the whole
        // table over it. Missing is only meaningful when the poll returned peers.
        return (
            isPollingEligible.value &&
            polledRowsById.value.size > 0 &&
            !polledRowsById.value.has(String(row.id))
        );
    }

    const state = computed<SearchLivePollState>(() => {
        if (rowIds.value.length === 0) {
            return 'idle';
        }
        if (payloadBytes.value >= LIVE_POLL_MAX_PAYLOAD_BYTES) {
            return 'payload-too-large';
        }
        if (consecutiveFailureCount.value >= LIVE_POLL_FAILING_AFTER_CONSECUTIVE_FAILURES) {
            return 'failing';
        }
        return 'active';
    });

    return {
        state,
        payloadBytes,
        maxPayloadBytes: LIVE_POLL_MAX_PAYLOAD_BYTES,
        pollIntervalMs: LIVE_RECORD_POLL_MS,
        /** Epoch ms of the last successful refresh (the initial search counts as one). */
        lastUpdatedAt: pollQuery.dataUpdatedAt,
        displayRows,
        isRowMissing,
    };
}
